import { SelectItem } from 'primeng/api';

import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { sort } from 'fast-sort';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { CommonService } from '../../common-api/common-api';
import { SystemDropDownListI18nViewModel } from '../../common-api/common-api/model/systemDropDownListI18nViewModel';
import {
  CacheService,
  MEMORY_CACHE_SERVICE,
} from '../../common/services/cache.service';
import { TranslatableDropdownOption } from '../models/translatable-dropdown-options';
import { isNullOrEmpty } from '../utils/common-functions';

const COMMON_DROPDOWN_CACHE_KEY = 'commonDropdownOptions';
const POSTCODE_LIST_CACHE_KEY = 'postcodeList';

/**
 * 共用下拉選單服務
 *
 * @export
 * @class DropdownOptionsService
 */
@Injectable({
  providedIn: 'root',
})
export class DropdownOptionsService {
  constructor(
    @Inject(MEMORY_CACHE_SERVICE) private cacheService: CacheService,
    private commonService: CommonService,
    private translateService: TranslateService
  ) {}

  /**
   * 取的共用下拉選單選項
   *
   * @param {string} [type]
   * @param {string} [code]
   * @param {boolean} [labelDisplayWithValue] display label in "key - value" format
   * @param {boolean} [useCache] 是否使用 cache
   * @return {*}  {Observable<TranslatableDropdownOption[]>}
   * @memberof DropdownOptionsService
   */
  getCommonDropdownOptions(
    type?: string,
    code?: string,
    labelDisplayWithValue?: boolean,
    useCache: boolean = true
  ): Observable<TranslatableDropdownOption[]> {
    // 使用 cache
    if (useCache) {
      const commonDropdownCache =
        this.cacheService.get<SystemDropDownListI18nViewModel[]>(
          COMMON_DROPDOWN_CACHE_KEY
        ) ?? [];
      const cacheOptions = commonDropdownCache.filter(
        (x) =>
          (isNullOrEmpty(type) || (!isNullOrEmpty(type) && x.type === type)) &&
          (isNullOrEmpty(code) ||
            (!isNullOrEmpty(code) && x.parentCode === code))
      );
      const observer =
        cacheOptions.length > 0
          ? of(cacheOptions)
          : this.commonService.dropDownListGet(code, type).pipe(
              tap((results) => {
                this.saveDropdownCacheData(results);
              })
            );
      return observer.pipe(
        map((results) => {
          const options = sort(results)
            .asc((x) => x.sortNo)
            .map(
              (x) =>
                new TranslatableDropdownOption(
                  this.translateService,
                  x,
                  labelDisplayWithValue ?? false
                )
            );
          return options;
        })
      );
    }

    const observer = this.commonService.dropDownListGet(code, type).pipe(
      map((results) => {
        const options = sort(results)
          .asc((x) => x.sortNo)
          .map(
            (x) =>
              new TranslatableDropdownOption(
                this.translateService,
                x,
                labelDisplayWithValue ?? false
              )
          );
        return options;
      })
    );
    return observer;
  }

  getPostcodeDropdownOptions(
    addressCode: string,
    useCache: boolean = true
  ): Observable<SelectItem[]> {
    // 使用 cache
    if (useCache) {
      const postcodeListCache =
        this.cacheService.get<{ addressCode: string; postcode: string }[]>(
          POSTCODE_LIST_CACHE_KEY
        ) ?? [];
      const cacheOptions = postcodeListCache
        .filter((x) => x.addressCode === addressCode)
        .map<SelectItem<string>>((x) => ({
          label: x.postcode,
          value: x.postcode,
        }));
      const observer =
        cacheOptions.length > 0
          ? of(cacheOptions)
          : this.commonService.addressAddressCodePostcodesGet(addressCode).pipe(
              tap((results) =>
                this.savePostcodeCacheData(addressCode, results)
              ),
              map((results) =>
                results.map<SelectItem<string>>((x) => ({ label: x, value: x }))
              )
            );
      return observer.pipe(tap((results) => sort(results).asc((x) => x.value)));
    }

    const observer = this.commonService
      .addressAddressCodePostcodesGet(addressCode)
      .pipe(
        map((results) =>
          results.map<SelectItem<string>>((x) => ({ label: x, value: x }))
        ),
        tap((results) => sort(results).asc((x) => x.value))
      );
    return observer;
  }

  private saveDropdownCacheData(
    options: SystemDropDownListI18nViewModel[]
  ): void {
    const commonDropdownCache =
      this.cacheService.get<SystemDropDownListI18nViewModel[]>(
        COMMON_DROPDOWN_CACHE_KEY
      ) ?? [];
    options.forEach((option) => {
      const idx = commonDropdownCache.findIndex(
        (x) => x.type === option.type && x.code === option.code
      );
      if (idx > -1)
        // 更新 cache
        commonDropdownCache[idx] = option;
      // 新增至 cache
      else commonDropdownCache.push(option);

      this.cacheService.add(COMMON_DROPDOWN_CACHE_KEY, commonDropdownCache);
    });
  }

  private savePostcodeCacheData(
    addressCode: string,
    postcodeList: string[]
  ): void {
    const postcodeListCache =
      this.cacheService.get<{ addressCode: string; postcode: string }[]>(
        POSTCODE_LIST_CACHE_KEY
      ) ?? [];
    postcodeList.forEach((postcode) => {
      const idx = postcodeListCache.findIndex(
        (x) => x.addressCode === addressCode && x.postcode === postcode
      );
      if (idx < 0)
        // 新增至 cache
        postcodeListCache.push({ addressCode, postcode });

      this.cacheService.add(POSTCODE_LIST_CACHE_KEY, postcodeListCache);
    });
  }
}
